import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { map, tap } from 'rxjs/operators';
import { InputField, InputType, config, ApiService, AuthService } from '@fgb/core';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SportsIllustratedAuthService {
  SportsIllustrated = 104;
  /**
   * This holds all the sports Illustrated settings required for sso,
   * this information will be read from the config js file
   * and also passed or defaulted from the login process.
   */
  constructor(
    private authService: AuthService,
    private api: ApiService,
    private cookieService: CookieService,
    private httpService: HttpClient
  ) {
  }

  /**
   * Attempt to login using sports Illustrated using redirect process.
   * This will call an endpoint to generate the login URL,
   * depending on the parameters passed.
   * This can also be used for registration, handled by the parameters passed.
   * @param returnVisitPath path for previous page button within SI login page,
   * e.g. /login (which is the default value)
   * @param successredirectPath redirect path after successfully authenticated
   * e.g. /si-callback (which is the default value)
   * @param mandatoryReg Enforces mandatory registration mode in which
   * users must register a full account rather than a lite or social account.
   * @param ssoMode ssoMode for URL, 0 = login, 1 = registration,
   * 2 = external login
   * @param clubId clubId to be passed to endpoint,
   * default to 1.
   */
  login(
    ssoMode: string = '0',
    clubId: number = 1
  ) {
    let state = this._createNonce(16) + 'O';
    let codeVerifier = this._createNonce(50);

    let isHttps = false;
    let redirectUrl = config.portalUrl;
    if (redirectUrl.lastIndexOf('/') != redirectUrl.length -1) {
      redirectUrl = redirectUrl + "/";
    }

    redirectUrl = redirectUrl +  'si-callback';

    if (redirectUrl.toUpperCase().indexOf('HTTPS') != -1) {
      isHttps = true;
    }

    this.cookieService.set('si-state', state, undefined, '/', undefined, isHttps, undefined);
    this.cookieService.set('si-codeverifier', codeVerifier, undefined, '/', undefined, isHttps, undefined);

    const loginUrlParameters: InputField[] = this.generateLoginParameters(redirectUrl,state, codeVerifier);

    return this.api.post(`sso/OAuth/GenerateUrl/${this.SportsIllustrated}/${clubId}`, loginUrlParameters).pipe(
      tap((generatedUrl: any) => {
        window.location.href = generatedUrl;
      })
    );
  }

  /**
   * This method is used to generate the parameters required for the callback process
   */
  generateCallBackParameters(code: string, state: string, codeverifier: string) {
    let generateLoginParams: InputField[] = [];

    let codeParam: InputField = {
      FieldName: 'Code',
      InputType: InputType.Text,
      Active: true,
      Value: code,
      ErrorMessage: '',
    };

    let stateParam: InputField = {
      FieldName: 'State',
      InputType: InputType.Text,
      Active: true,
      Value: state,
      ErrorMessage: '',
    };

    let codeVerfierParam: InputField = {
      FieldName: 'CodeVerifier',
      InputType: InputType.Text,
      Active: true,
      Value: codeverifier,
      ErrorMessage: '',
    };

    generateLoginParams.push(codeParam);
    generateLoginParams.push(stateParam);
    generateLoginParams.push(codeVerfierParam);

    return generateLoginParams;
  }

  /**
   * This method is used to generate the parameters required for the login process
   */
  generateLoginParameters(successredirectPath: string, state: string, codeVerifier: string) {
    let generateLoginParams: InputField[] = [];
    
    let returnVisitParam: InputField = {
      FieldName: 'RedirectUrl',
      InputType: InputType.Text,
      Active: true,
      Value: successredirectPath,
      ErrorMessage: '',
    };

    let StateParam: InputField = {
      FieldName: 'State',
      InputType: InputType.Text,
      Active: true,
      Value: state,
      ErrorMessage: '',
    };

    let codeVerifierParam: InputField = {
      FieldName: 'CodeVerifier',
      InputType: InputType.Text,
      Active: true,
      Value: codeVerifier,
      ErrorMessage: '',
    };

    generateLoginParams.push(returnVisitParam);
    generateLoginParams.push(StateParam);
    generateLoginParams.push(codeVerifierParam);

    return generateLoginParams;
  }

  /**
   * This method is used to authenticate a token
   * that comes back from the Sports Illustrated login page
   * and log the user in via the tokens returned
   * from the response.
   * @param jwtToken JWT Token returned from
   * Sports Illustrated, to be authenticated and
   * passed to given endpoint.
   * @param clubId clubId to be passed to endpoint,
   * default to 1.
   * @returns boolean from status of response.
   */
  validateCallBack(code: string, codeVerifier: string, state: string, clubId: number = 1) {

    
    let headersList = new HttpHeaders({ 'Content-Type': 'application/json' });
    const loginUrlParameters: InputField[] = this.generateCallBackParameters(code, state, codeVerifier);

    return firstValueFrom(this.httpService
          .post(`${config.baseApiUrl}/api/sso/v2/OAuth/${this.SportsIllustrated}/${clubId}`, loginUrlParameters, { headers: headersList })
          .pipe(
            tap((authResponse: any) => {
              if (authResponse) {
                console.log("logging customer in")

                const accessToken = authResponse.tokens.AccessToken;             

                this.authService.loginUsingToken(accessToken, authResponse.tokens.RefreshToken, !!authResponse.auth.LinkedAccounts ? '/choose-account' : '/');               
              }
              else {
                // invalid response
                console.log("error");
              }
            })));
  }

  /**
   * Log out of the portal and provider (Sports Illustrated).
   * Passes provider tokens and provider ID
   * to logout for the logout endpoint
   * with portal provider details.
   */
  logout() {
    let providerTokens: { [name: string]: string | null } = {};
    providerTokens.providerRefreshToken = localStorage.getItem('SIRefreshToken');
    providerTokens.providerAccessToken = localStorage.getItem('SIAccessToken');

    return this.authService.logout(this.SportsIllustrated, providerTokens);
  }

  /**
   * This method is used to authenticate a domain
   * token used from the _si_sso_upid cookie given
   * from Sports Illustrated domain and log the user
   * using tokens returned.
   * @param domainCookie domain cookie to be
   * authenticated and passed to endpoint.
   * @param clubId clubId to be passed to endpoint,
   * default to 1.
   * @returns boolean from status of response.
   */
  loginUsingToken(domainCookie: string, clubId: number = 1) {
    let siDomainCookie = '';
    if (domainCookie != '' && domainCookie != undefined) {
      siDomainCookie = domainCookie;
    }

    return this.api.get(`sso/OAuth/${this.SportsIllustrated}/${siDomainCookie}/${clubId}`).pipe(
      tap((authResponse: any) => {
        if (authResponse) {
          this.authService.loginUsingToken(authResponse.tokens.AccessToken, authResponse.tokens.RefreshToken, '/');
          localStorage.setItem('SIRefreshToken', authResponse.auth.RefreshToken.toString());
          localStorage.setItem('SIAccessToken', authResponse.auth.AccessToken.toString());
        }
      }),
      map((authResponse: any) => {
        if ((authResponse.status = 200)) {
          return true;
        } else {
          return false;
        }
      })
    );
  }

  /**
   * Check if a named cookie exists.
   * @param cookieName cookie name to be checked.
   * @returns boolean value depending if cookie name
   * exists.
   */
  hasNamedCookie(cookieName: string): boolean {
    if (this.cookieService.check(cookieName)) {
      return true;
    }
    return false;
  }

  /**
   * Create a nonce for the state
   *
   * @param length length of the nonce
   */
   private _createNonce(length: number) {
    let charset = '0123456789ABCDEFGHIJKLMNOPQRSTUVXYZabcdefghijklmnopqrstuvwxyz-.';
    let result = '';

    while (length > 0) {
      var bytes = new Uint8Array(16);
      var random = window.crypto.getRandomValues(bytes);

      random.forEach(function (c) {
        if (length == 0) {
          return;
        }
        if (c < charset.length) {
          result += charset[c];
          length--;
        }
      });
    }
    return result + '_';
  }
}
